/* eslint-disable max-len */
/* eslint-disable linebreak-style */
import { v4 as uuidV4 } from 'uuid';
import fixWebmDuration from 'webm-duration-fix';
import { getRoomName } from '../../../base/conference/functions';
import { MEDIA_TYPE } from '../../../base/media/constants';
import i18next from '../../../base/i18n/i18next';
import { PARTICIPANT_ROLE } from '../../../base/participants/constants';
import { getLocalTrack, getTrackState } from '../../../base/tracks/functions';
import { isEmbedded } from '../../../base/util/embedUtils';
import { showWarningNotification } from '../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../notifications/constants';
import { getBrand } from '../../../utils/brandUtils';
import { getBrandFont, getBrandImageInRoom } from '../../../utils/brandDomainUtils';
import { stopLocalVideoRecording } from '../../actions.any';
const getMimeType = () => {
    // Codec ottimizzati per evitare problemi avc1 sui cambi risoluzione
    const possibleTypes = [
        'video/mp4;codecs=h264,aac', // H264 + AAC - Più flessibile per cambi risoluzione
        'video/mp4;codecs=h264', // H264 generico - Lascia decidere al browser
        'video/webm;codecs=vp8,opus', // VP8 - Stabile per cambi dinamici
        'video/webm;codecs=vp9,opus', // VP9 - Qualità elevata
        'video/mp4', // MP4 base - Fallback (potrebbe scegliere avc1)
        'video/webm' // WebM base - Ultimo resort
    ];
    for (const type of possibleTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    throw new Error('No MIME Type supported by MediaRecorder');
};
// Bitrate conservativo per compatibilità universale Windows/macOS
const getVideoBitRate = (width, height) => {
    const pixels = width * height;
    // Bitrate più conservativo per migliore compatibilità
    if (pixels >= 1920 * 1080)
        return 3000000; // 1080p: 3Mbps - Compatibile
    if (pixels >= 1280 * 720)
        return 2000000; // 720p: 2Mbps - Standard
    if (pixels >= 854 * 480)
        return 1200000; // 480p: 1.2Mbps
    return 800000; // <480p: 800kbps
};
const VIDEO_BIT_RATE = 2000000; // Default 2Mbps - Universalmente compatibile
const MAX_SIZE = Number.MAX_SAFE_INTEGER; // Illimitato
const MEMORY_SAFETY_THRESHOLD = 536870912; // 512MB - soglia di sicurezza memoria
const AUTO_SAVE_INTERVAL = 600000; // Auto-save ogni 10 minuti
const brand = getBrand();
// Helper function per disegnare l'icona moderatore
const drawModeratorIcon = (ctx, x, y, size = 12) => {
    // Salva stato canvas
    ctx.save();
    // Posiziona e scala
    ctx.translate(x, y);
    ctx.scale(size / 32, size / 32); // L'SVG originale è 32x32
    // Disegna stella moderatore VUOTA (solo outline) usando il path reale di IconModerator
    ctx.fillStyle = 'transparent'; // Trasparente all'interno
    ctx.strokeStyle = '#ffffff'; // Bordo bianco
    ctx.lineWidth = 2; // Bordo più spesso per migliore visibilità (adattato per scala 32x32)
    // Path reale dell'icona host.svg (IconModerator)
    const moderatorPath = "M16 20.563l5 3-1.313-5.688 4.438-3.875-5.875-0.5-2.25-5.375-2.25 5.375-5.875 0.5 4.438 3.875-1.313 5.688zM29.313 12.313l-7.25 6.313 2.188 9.375-8.25-5-8.25 5 2.188-9.375-7.25-6.313 9.563-0.813 3.75-8.813 3.75 8.813z";
    const path = new Path2D(moderatorPath);
    ctx.stroke(path); // Solo stroke, nessun fill
    // Ripristina stato canvas
    ctx.restore();
};
// Lazily initialize.
let preferredMediaType;
const LocalRecordingManager = {
    canvas: undefined,
    ctx: null,
    dispatch: null,
    cachedScreenSharePaths: undefined,
    imageBrand: null,
    recordInterval: undefined,
    paintID: 0,
    recordingData: [],
    recorder: undefined,
    stream: undefined,
    audioContext: undefined,
    audioDestination: undefined,
    roomName: '',
    totalSize: MAX_SIZE,
    currentMemoryUsage: 0,
    memoryWarningShown: false,
    criticalMemoryWarningShown: false,
    recordingSaved: false,
    isLeavingFromButton: false,
    selfRecording: {
        on: false,
        withVideo: false
    },
    get mediaType() {
        if (this.selfRecording.on && !this.selfRecording.withVideo) {
            return 'audio/webm;';
        }
        if (!preferredMediaType) {
            preferredMediaType = getMimeType();
        }
        return preferredMediaType;
    },
    /**
     * Initializes audio context used for mixing audio tracks.
     *
     * @returns {void}
     */
    initializeAudioMixer() {
        this.audioContext = new AudioContext();
        this.audioDestination = this.audioContext.createMediaStreamDestination();
    },
    /**
     * Mixes multiple audio tracks to the destination media stream.
     *
     * @param {MediaStream} stream - The stream to mix.
     * @returns {void}
     * */
    mixAudioStream(stream) {
        if (stream.getAudioTracks().length > 0 && this.audioDestination) {
            this.audioContext?.createMediaStreamSource(stream).connect(this.audioDestination);
        }
    },
    /**
     * Adds audio track to the recording stream.
     *
     * @param {MediaStreamTrack} track - The track to be added.
     * @returns {void}
     */
    addAudioTrackToLocalRecording(track) {
        if (this.selfRecording.on) {
            return;
        }
        if (track) {
            const stream = new MediaStream([track]);
            this.mixAudioStream(stream);
        }
    },
    /**
     * Returns a filename based ono the Jitsi room name in the URL and timestamp.
     *
     * @returns {string}
     * */
    getFilename() {
        const now = new Date();
        const dateString = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}_${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}`;
        return `video_${this.roomName}_${dateString}`;
    },
    /**
     * Saves the recording to file with format optimization.
     *
     * @param {Array} recordingData - The recording data.
     * @param {string} filename - The name of the file.
     * @returns {void}
     * */
    async saveRecording(recordingData, filename) {
        try {
            // Verifica che ci siano dati validi da salvare
            if (!recordingData || recordingData.length === 0) {
                console.warn('Nessun dato da salvare nella registrazione');
                return;
            }
            // @ts-ignore - Bypassa conflitto TypeScript con BlobOptions
            const blob = new Blob(recordingData, { type: this.mediaType });
            // Verifica che il blob abbia contenuto
            if (blob.size === 0) {
                console.warn('La registrazione è vuota, nessun file da salvare');
                return;
            }
            // Corregge durata per file WebM (risolve problemi di compatibilità)
            let finalBlob = blob;
            if (this.mediaType.includes('webm') && blob.size > 1000) {
                try {
                    // @ts-ignore
                    finalBlob = await fixWebmDuration(blob);
                }
                catch (error) {
                    console.warn('Impossibile correggere durata WebM:', error);
                    finalBlob = blob; // Usa blob originale in caso di errore
                }
            }
            // @ts-ignore -- URL.createObjectURL/revokeObjectURL not typed in this ts-loader context
            const url = URL.createObjectURL(finalBlob);
            const a = document.createElement('a');
            // Determina estensione - Priorità MP4 per compatibilità seeking universale
            let extension = 'mp4'; // default per massima compatibilità
            // MP4 per compatibilità universale Windows/macOS player nativi
            if (this.mediaType.includes('mp4')) {
                extension = 'mp4';
            }
            else if (this.mediaType.includes('webm')) {
                extension = 'webm';
            }
            a.style.display = 'none';
            a.href = url;
            a.download = `${filename}.${extension}`;
            a.click();
            // Cleanup
            // @ts-ignore -- URL.createObjectURL/revokeObjectURL not typed in this ts-loader context
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
        catch (error) {
            console.error('Errore salvataggio registrazione:', error);
        }
    },
    /**
     * Monitora l'uso della memoria per warning
     */
    checkMemoryUsage() {
        this.currentMemoryUsage = this.recordingData.reduce((total, chunk) => total + chunk.size, 0);
        // Solo warning informativi - nessun backup
        if (this.currentMemoryUsage > MEMORY_SAFETY_THRESHOLD) {
            if (this.currentMemoryUsage > MEMORY_SAFETY_THRESHOLD * 2 && !this.memoryWarningShown) {
                // Mostra avviso visibile all'utente (solo una volta)
                this.memoryWarningShown = true;
                if (this.dispatch) {
                    this.dispatch(showWarningNotification({
                        titleKey: 'recording.highMemoryUsageTitle',
                        descriptionKey: 'recording.highMemoryUsageMessage'
                    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                }
                else {
                    console.warn('Memoria molto alta - considera di fermare la registrazione se il meeting è finito');
                }
            }
        }
    },
    /**
     * Stops local recording.
     *
     * @returns {void}
     * */
    stopLocalRecording() {
        this.forceStopAndSave?.();
        // Pulisci flag restart quando registrazione fermata manualmente
        try {
            localStorage.removeItem('jitsi_was_recording_before_reload');
            localStorage.removeItem('jitsi_recording_timestamp');
            localStorage.removeItem('jitsi_recording_onlyself');
            localStorage.removeItem('jitsi_reload_from_stay');
            localStorage.removeItem('jitsi_last_meeting_exit_type');
        }
        catch (e) {
            // Ignora errori localStorage
        }
        if (this.canvas?.parentElement) {
            this.canvas.parentElement.removeChild(this.canvas);
        }
        this.canvas = undefined;
        this.ctx = null;
        this.audioContext = undefined;
        this.audioDestination = undefined;
        this.totalSize = MAX_SIZE;
        this.memoryWarningShown = false;
        this.criticalMemoryWarningShown = false;
        // Timeout di sicurezza per dati tardivi
        setTimeout(() => {
            if (this.recordingData.length > 0 && !this.recordingSaved) {
                this.recordingSaved = true;
                const dataToSave = [...this.recordingData];
                this.recordingData = [];
                this.saveRecording(dataToSave, this.getFilename());
            }
        }, 1000);
    },
    createLayer(canvas) {
        const layer = document.createElement('canvas');
        // @ts-ignore
        layer.width = canvas.width;
        // @ts-ignore
        layer.height = canvas.height;
        const ctx = layer.getContext('2d');
        // Ottimizzazioni rendering per qualità migliore
        if (ctx) {
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            // @ts-ignore - Proprietà sperimentale per qualità testo
            ctx.textRendering = 'geometricPrecision';
        }
        return ctx;
    },
    recordVideos() {
        // Funzione helper per inizializzare canvas dinamicamente
        const ensureCanvasExists = () => {
            if (!this.canvas) {
                this.canvas = document.createElement('canvas');
                this.canvas.style.imageRendering = 'auto';
                (document.body || document.documentElement).appendChild(this.canvas);
                this.ctx = this.canvas.getContext('2d', {
                    willReadFrequently: true,
                    alpha: false,
                    desynchronized: false,
                    powerPreference: 'default'
                });
                // Inizializza anche audio mixer se non presente
                if (!this.audioContext) {
                    this.initializeAudioMixer();
                }
            }
        };
        const resizeCanvas = (cssWidth, cssHeight) => {
            if (!this.canvas || !this.ctx) {
                return;
            }
            const dpr = window.devicePixelRatio || 1;
            const targetWidth = Math.floor(cssWidth * dpr);
            const targetHeight = Math.floor(cssHeight * dpr);
            // Evita resize inutili (che resettano il context)
            if (this.canvas.width !== targetWidth || this.canvas.height !== targetHeight) {
                this.canvas.width = targetWidth;
                this.canvas.height = targetHeight;
                this.canvas.style.width = `${cssWidth}px`;
                this.canvas.style.height = `${cssHeight}px`;
                // Reset e applica scala DPR
                this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            }
        };
        this.imageBrand = new Image();
        this.imageBrand.src = '../../../../../' + getBrandImageInRoom();
        // Preload dell'immagine per calcoli precisi delle proporzioni
        this.imageBrand.onload = () => {
            console.debug('Brand image loaded:', this.imageBrand.naturalWidth + 'x' + this.imageBrand.naturalHeight);
        };
        // Helper per calcolare dimensioni brand mantenendo proporzioni
        const getBrandDimensions = (targetWidth = 180) => {
            if (!this.imageBrand.complete || this.imageBrand.naturalWidth === 0) {
                return { width: targetWidth, height: 25 }; // Fallback se immagine non caricata
            }
            const aspectRatio = this.imageBrand.naturalWidth / this.imageBrand.naturalHeight;
            const calculatedHeight = targetWidth / aspectRatio;
            return {
                width: targetWidth,
                height: Math.round(calculatedHeight)
            };
        };
        let cachedSharerWebcam = null;
        let lastWebcamCheck = 0;
        const WEBCAM_CACHE_DURATION = 100; // Cache solo per 100ms - mantiene fluidità
        // Cache per rilevamento screen sharing - evita freeze durante transizione
        let cachedScreenShareElements = {
            localVideo: null,
            remoteVideo: null,
            lastCheck: 0
        };
        const SCREEN_SHARE_CACHE_DURATION = 200; // Cache più lunga per stabilità transizione
        const paint = () => {
            // Controllo sicurezza canvas: assicurati che esista sempre durante lo screen sharing
            if (!this.canvas || !this.ctx) {
                const hasActiveScreenShare = document.querySelector('#localScreenShare_container') ||
                    Array.from(document.querySelectorAll('span[id]')).find(span => /^participant_[a-zA-Z0-9]+-v\d+$/.test(span.id))?.querySelector('video');
                // Ricrea il canvas quando necessario, senza toccare lo stream
                if (hasActiveScreenShare) {
                    ensureCanvasExists();
                }
                if (!this.canvas || !this.ctx) {
                    console.warn('Canvas non disponibile - interrompi paint loop');
                    return;
                }
            }
            const store = APP.store;
            const { local, remote } = store.getState()['features/base/participants'];
            // Risoluzione dinamica basata sul numero di partecipanti per evitare blocchi
            const totalParticipants = Object.keys(remote).length + 1; // +1 per local
            let elementWidth, elementHeight;
            if (totalParticipants <= 4) {
                // 1-4 partecipanti: qualità massima
                elementWidth = 1920;
                elementHeight = 1080;
            }
            else if (totalParticipants <= 9) {
                // 5-9 partecipanti: qualità media-alta
                elementWidth = 1280;
                elementHeight = 720;
            }
            else {
                // 10+ partecipanti: qualità media per evitare blocchi
                elementWidth = 960;
                elementHeight = 540;
            }
            const paddingX = 10;
            const paddingY = 10;
            let videos;
            let numElements = 1;
            let numColumns = 1;
            let numRows = 1;
            const whiteboardVideo = document.querySelector(".excalidraw__canvas");
            // Rilevamento screen sharing con cache per evitare freeze durante transizione
            const now = Date.now();
            if ((now - cachedScreenShareElements.lastCheck) > SCREEN_SHARE_CACHE_DURATION) {
                // Aggiorna cache solo quando necessario
                cachedScreenShareElements.localVideo = document.querySelector('#localScreenShare_container');
                const matchingSpan = Array.from(document.querySelectorAll('span[id]')).find(span => /^participant_[a-zA-Z0-9]+-v\d+$/.test(span.id));
                cachedScreenShareElements.remoteVideo = matchingSpan?.querySelector('video') || null;
                cachedScreenShareElements.lastCheck = now;
            }
            const localScreenShareVideo = cachedScreenShareElements.localVideo;
            const videoEl = cachedScreenShareElements.remoteVideo;
            // Se esiste screen share (locale o remoto), renderizzalo con logica specifica per tipo
            if (localScreenShareVideo || videoEl) {
                const videoElement = (localScreenShareVideo || videoEl);
                if (!this.canvas || !this.ctx) {
                    console.warn('Canvas non disponibile per screen share');
                    return;
                }
                resizeCanvas(elementWidth, elementHeight);
                // Rendering condivisione schermo (schede Chrome, schermo intero, finestre)
                try {
                    if (videoElement.readyState >= 2 &&
                        videoElement.videoWidth > 0 &&
                        !videoElement.paused) {
                        this.ctx.drawImage(videoElement, 0, 0, elementWidth, elementHeight);
                    }
                    else {
                        this.ctx.fillStyle = '#000000';
                        this.ctx.fillRect(0, 0, elementWidth, elementHeight);
                    }
                }
                catch (error) {
                    console.debug('Errore temporaneo rendering condivisione:', error);
                    this.ctx.fillStyle = '#1a1a1a';
                    this.ctx.fillRect(0, 0, elementWidth, elementHeight);
                }
                const layerSharerInfo = this.createLayer(this.canvas);
                layerSharerInfo.fillStyle = 'white';
                layerSharerInfo.font = `bold 16px ${getBrandFont()}`; // Font ridotto per migliore proporzione
                // Disegna brand con proporzioni corrette
                const brandDims = getBrandDimensions(180);
                layerSharerInfo.drawImage(this.imageBrand, 20, 20, brandDims.width, brandDims.height);
                let sharerName = i18next.t('localRecording.screenSharing');
                // Thumbnail webcam del condivisore (se disponibile)
                let sharerWebcam = null;
                if (localScreenShareVideo) {
                    // Condivisione locale - ricerca ottimizzata (evita scan completo DOM)
                    sharerWebcam = document.querySelector('video[id*="localVideo"]') ||
                        document.querySelector('.videocontainer video');
                    if (local?.name) {
                        sharerName = i18next.t('localRecording.screenSharingOf', { name: local.name });
                    }
                }
                else if (videoEl) {
                    // Condivisione remota - cerca webcam del partecipante e nome
                    // Trova lo span contenitore della condivisione schermo
                    const screenShareSpan = videoEl.closest('span[id]');
                    const sharerSpanId = screenShareSpan?.id;
                    if (sharerSpanId) {
                        // Estrai l'ID partecipante dalla condivisione schermo (format: participant_ID-v0)
                        const participantId = sharerSpanId.replace(/-v\d+$/, '');
                        // Estrai l'ID reale del partecipante (senza il prefisso participant_)
                        const realParticipantId = participantId.split('_')[1];
                        // Cerca il nome dal Redux store (più affidabile del DOM)
                        const remoteParticipant = realParticipantId ? remote.get(realParticipantId) : null;
                        if (remoteParticipant?.name) {
                            sharerName = i18next.t('localRecording.screenSharingOf', { name: remoteParticipant.name });
                        }
                        else {
                            // Fallback: cerca nel DOM se non trovato nel Redux store
                            const sharerNameElement = document.querySelector(`#${participantId}_name`) ||
                                document.querySelector(`[id*="${participantId}"]:not([id*="-v"]) .displayname`) ||
                                document.querySelector(`[id*="${realParticipantId}"] .displayname`);
                            if (sharerNameElement?.textContent && sharerNameElement.textContent.trim()) {
                                const name = sharerNameElement.textContent.trim();
                                sharerName = i18next.t('localRecording.screenSharingOf', { name });
                            }
                        }
                        // Cerca il video webcam dello stesso partecipante con diversi pattern possibili
                        const webcamSpan = document.querySelector(`span[id="${participantId}"]`) ||
                            document.querySelector(`span[id*="${participantId}"]:not([id*="-v"])`) ||
                            document.querySelector(`[id="${participantId}_container"]`);
                        sharerWebcam = webcamSpan?.querySelector('video');
                        // Se non trova con i pattern standard, cerca in tutti i video
                        if (!sharerWebcam || sharerWebcam.videoWidth === 0) {
                            const allVideos = Array.from(document.querySelectorAll('video'));
                            sharerWebcam = allVideos.find(v => v.parentElement?.id?.includes(realParticipantId) &&
                                !v.parentElement?.id?.includes('-v') &&
                                v.videoWidth > 0);
                        }
                    }
                }
                // Disegna thumbnail webcam se disponibile e attiva
                if (sharerWebcam &&
                    sharerWebcam.videoWidth > 0 &&
                    sharerWebcam.videoHeight > 0 &&
                    sharerWebcam.srcObject &&
                    !sharerWebcam.paused &&
                    sharerWebcam.readyState >= 2) { // HAVE_CURRENT_DATA o superiore
                    // Controllo ottimizzato: usa cache per evitare verifiche intensive
                    let hasActiveVideoTrack = true; // Assumi attivo per evitare controlli costosi
                    // Verifica solo se necessario (ogni 10 frame circa)
                    if (Math.random() < 0.1) {
                        const mediaStream = sharerWebcam.srcObject;
                        const videoTracks = mediaStream?.getVideoTracks() || [];
                        hasActiveVideoTrack = videoTracks.some(track => track.enabled && track.readyState === 'live');
                    }
                    if (hasActiveVideoTrack) {
                        const thumbnailWidth = 165; // 10% più grande per migliore visibilità  
                        const thumbnailHeight = 92; // Mantiene proporzione 16:9 con 10% di aumento
                        const thumbnailX = elementWidth - thumbnailWidth - 20; // 20px dal bordo destro
                        const thumbnailY = 20; // 20px dall'alto
                        // Bordo nero per il thumbnail
                        layerSharerInfo.fillStyle = 'rgba(0, 0, 0, 0.8)';
                        layerSharerInfo.fillRect(thumbnailX - 2, thumbnailY - 2, thumbnailWidth + 4, thumbnailHeight + 4);
                        // Disegna la webcam del condivisore
                        layerSharerInfo.drawImage(sharerWebcam, thumbnailX, thumbnailY, thumbnailWidth, thumbnailHeight);
                        // Reset colore testo
                        layerSharerInfo.fillStyle = 'white';
                    }
                }
                // Sfondo sfumato per il nome del condivisore (con spazio per l'icona)
                const iconSpace = 25; // Spazio per l'icona screen share
                const textMetrics = layerSharerInfo.measureText(sharerName);
                const textWidth = textMetrics.width;
                const textHeight = 20; // Altezza approssimativa del testo
                const bgX = 15; // Padding dal bordo
                const bgY = elementHeight - 35; // Posizione originale per condivisione schermo
                const bgWidth = textWidth + iconSpace + 10; // Larghezza sfondo con padding + icona
                const bgHeight = textHeight + 5; // Altezza sfondo
                // Gradiente dal scuro (sotto) al trasparente (sopra)
                const gradient = layerSharerInfo.createLinearGradient(0, bgY + bgHeight, 0, bgY);
                gradient.addColorStop(0, 'rgba(0, 0, 0, 0.7)'); // Scuro sotto
                gradient.addColorStop(1, 'rgba(0, 0, 0, 0)'); // Trasparente sopra
                layerSharerInfo.fillStyle = gradient;
                layerSharerInfo.fillRect(bgX, bgY, bgWidth, bgHeight);
                // Usa l'icona IconScreenshare reale con il suo path SVG
                layerSharerInfo.save();
                layerSharerInfo.translate(20, elementHeight - 30);
                layerSharerInfo.scale(0.75, 0.75); // Scala per renderla più piccola
                layerSharerInfo.fillStyle = 'transparent';
                layerSharerInfo.strokeStyle = '#ffffff';
                layerSharerInfo.lineWidth = 1.5;
                // Path SVG cache - evita parsing costoso ad ogni frame
                if (!this.cachedScreenSharePaths) {
                    const screenSharePath1 = "M14.8464 6.22103C14.2729 5.67701 13.327 6.08352 13.327 6.87396V8.60046C8.62852 8.92199 4.8467 12.239 4.52225 16.4471C4.48676 16.9074 4.78878 17.2544 5.14194 17.3656C5.48464 17.4735 5.90666 17.373 6.15309 17.0188C8.33798 13.8785 11.4845 13.1373 13.327 13.0452V15.2002C13.327 15.9986 14.2893 16.4017 14.8583 15.8416L19.1656 11.6016C19.5284 11.2444 19.523 10.6577 19.1536 10.3073L14.8464 6.22103ZM14.827 10.0749V8.27022L17.6708 10.9682L14.827 13.7676V11.5731L14.109 11.5424C12.5831 11.4771 9.42974 11.7986 6.73783 14.1317C7.99338 11.7842 10.7375 10.0749 14.077 10.0749H14.827Z";
                    const screenSharePath2 = "M4.5 3C2.84315 3 1.5 4.34315 1.5 6V17.25C1.5 18.9069 2.84315 20.25 4.5 20.25H5.25C4.83579 20.25 4.5 20.5858 4.5 21C4.5 21.4142 4.83579 21.75 5.25 21.75H18.75C19.1642 21.75 19.5 21.4142 19.5 21C19.5 20.5858 19.1642 20.25 18.75 20.25H19.5C21.1569 20.25 22.5 18.9069 22.5 17.25V6C22.5 4.34315 21.1569 3 19.5 3H4.5ZM19.5 4.5H4.5C3.67157 4.5 3 5.17157 3 6V17.25C3 18.0784 3.67157 18.75 4.5 18.75H19.5C20.3284 18.75 21 18.0784 21 17.25V6C21 5.17157 20.3284 4.5 19.5 4.5Z";
                    this.cachedScreenSharePaths = {
                        path1: new Path2D(screenSharePath1),
                        path2: new Path2D(screenSharePath2)
                    };
                }
                // Usa le path cached
                layerSharerInfo.stroke(this.cachedScreenSharePaths.path1);
                layerSharerInfo.stroke(this.cachedScreenSharePaths.path2);
                layerSharerInfo.restore();
                // Reset colore per il testo
                layerSharerInfo.fillStyle = 'white';
                layerSharerInfo.fillText(sharerName, 20 + iconSpace, // Posizionamento dopo l'icona
                elementHeight - 15 // Posizionamento con 15px dal bordo inferiore (più in basso nella parte opaca)
                );
                this.ctx.drawImage(layerSharerInfo.canvas, 0, 0);
                // Throttling differenziato in base alla risoluzione del contenuto condiviso
                const isHighRes = videoElement.videoWidth >= 1280 && videoElement.videoHeight >= 720;
                const frameDelay = isHighRes ? 16 : 33; // ~60 FPS per alta risoluzione, ~30 FPS per bassa
                setTimeout(() => {
                    this.paintID = requestAnimationFrame(paint);
                }, frameDelay);
                return;
            }
            // Fallback: nessuno condivide schermo e il moderatore non espone la lavagna -> mostriamo webcam in griglia            // Fallback: nessuno condivide schermo e il moderatore non espone la lavagna -> mostriamo webcam in griglia
            if (whiteboardVideo === null || whiteboardVideo.width === 0) {
                videos = Array.from(document.querySelectorAll('span.display-video video'));
                // Filtra solo video con contenuto attivo
                const activeVideos = videos.filter(v => v.videoWidth > 0 &&
                    v.videoHeight > 0 &&
                    v.srcObject &&
                    !v.paused &&
                    v.readyState >= 2);
                numElements = activeVideos.length;
                // Se nessuna webcam è attiva, mostra schermata informativa
                if (numElements === 0) {
                    resizeCanvas(elementWidth, elementHeight);
                    // Sfondo scuro
                    this.ctx.fillStyle = '#1a1a1a';
                    this.ctx.fillRect(0, 0, elementWidth, elementHeight);
                    const layerInfo = this.createLayer(this.canvas);
                    layerInfo.fillStyle = 'white';
                    layerInfo.font = `bold 32px ${getBrandFont()}`;
                    // Disegna brand con proporzioni corrette
                    const brandDims = getBrandDimensions(180);
                    layerInfo.drawImage(this.imageBrand, 20, 20, brandDims.width, brandDims.height);
                    const message = i18next.t('localRecording.noWebcamActive');
                    const messageWidth = layerInfo.measureText(message).width;
                    layerInfo.fillText(message, (elementWidth - messageWidth) / 2, elementHeight / 2);
                    this.ctx.drawImage(layerInfo.canvas, 0, 0);
                    setTimeout(() => {
                        this.paintID = requestAnimationFrame(paint);
                    }, 100); // Update più lento quando non c'è contenuto
                    return;
                }
                videos = activeVideos;
                numColumns = Math.ceil(Math.sqrt(numElements));
                numRows = Math.ceil(numElements / numColumns);
            }
            // Controllo sicurezza canvas per griglia
            if (!this.canvas || !this.ctx) {
                console.warn('Canvas non disponibile per griglia');
                return;
            }
            resizeCanvas(((elementWidth + paddingX) * numColumns) + paddingX, ((elementHeight + paddingY) * numRows) + paddingY);
            const layerParticipantName = this.createLayer(this.canvas);
            layerParticipantName.fillStyle = 'white';
            // Font scalato in base al numero di partecipanti per mantenere proporzioni 
            // Per una sola webcam (full screen), usa dimensione ridotta per migliore proporzione
            let scaledFontSize;
            if (numElements === 1) {
                scaledFontSize = 20; // Ridotto per full screen
            }
            else {
                scaledFontSize = Math.max(16, Math.min(28, 28 / Math.sqrt(numElements)));
            }
            layerParticipantName.font = `bold ${scaledFontSize}px ${getBrandFont()}`;
            // Disegna brand con proporzioni corrette
            const brandDims = getBrandDimensions(180);
            layerParticipantName.drawImage(this.imageBrand, paddingX + 20, paddingY + 20, brandDims.width, brandDims.height);
            let name;
            if (whiteboardVideo !== null && whiteboardVideo.width !== 0) {
                this.ctx.drawImage(whiteboardVideo, 0, 0, elementWidth, elementHeight);
            }
            else {
                for (let i = 0; i < numElements; i++) {
                    const elementX = ((i % numColumns) * (elementWidth + paddingX)) + paddingX;
                    const elementY = (Math.floor(i / numColumns) * (elementHeight + paddingY)) + paddingY;
                    name = '';
                    let isModerator = false;
                    // @ts-ignore
                    if (videos[i].id === 'localVideo_container') {
                        name = local?.name || '';
                        isModerator = local?.role === PARTICIPANT_ROLE.MODERATOR;
                    }
                    else {
                        // @ts-ignore
                        const parentId = document.querySelector("#" + videos[i].id)?.parentElement?.id;
                        name = document.querySelector("#" + parentId + "_name")?.textContent || '';
                        // Trova il partecipante remoto corrispondente per controllare se è moderatore
                        const participantId = parentId?.replace('_container', '').split('_')[1];
                        const remoteParticipant = participantId ? remote.get(participantId) : null;
                        isModerator = remoteParticipant?.role === PARTICIPANT_ROLE.MODERATOR;
                    }
                    // Sfondo sfumato per il nome del partecipante (include spazio per icona moderatore)
                    if (name) {
                        const textMetrics = layerParticipantName.measureText(name);
                        const textWidth = textMetrics.width;
                        const textHeight = scaledFontSize + 2; // Altezza basata su font size
                        const iconSpace = isModerator ? 18 : 0; // Spazio per l'icona moderatore (12px + 6px padding)
                        const bgX = elementX + 15; // Padding dal bordo
                        const bgY = elementY + elementHeight - 40; // Posizione sfondo più in alto vicino al testo
                        const bgWidth = textWidth + iconSpace + 10; // Larghezza sfondo con padding + eventuale icona
                        const bgHeight = textHeight + 5; // Altezza sfondo
                        // Gradiente dal scuro (sotto) al trasparente (sopra)
                        const gradient = layerParticipantName.createLinearGradient(0, bgY + bgHeight, 0, bgY);
                        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.7)'); // Scuro sotto
                        gradient.addColorStop(1, 'rgba(0, 0, 0, 0)'); // Trasparente sopra
                        layerParticipantName.fillStyle = gradient;
                        layerParticipantName.fillRect(bgX, bgY, bgWidth, bgHeight);
                        // Reset colore per il testo
                        layerParticipantName.fillStyle = 'white';
                    }
                    layerParticipantName.fillText(name, elementX + 20, elementY + elementHeight - 15); // Posizionamento con 15px dal bordo inferiore (più in basso nella parte opaca)
                    // Disegna icona moderatore DENTRO lo sfondo se necessario
                    if (isModerator && name) {
                        const textMetrics = layerParticipantName.measureText(name);
                        const iconX = elementX + 20 + textMetrics.width + 6; // 6px di spazio dopo il nome
                        // Allineamento Y proporzionato: più in basso per single webcam
                        const iconY = numElements === 1
                            ? elementY + elementHeight - 30 // Stella più in alto per visualizzazione singola
                            : elementY + elementHeight - 35; // Posizione originale per griglia
                        // Dimensione icona proporzionata: più piccola per full screen
                        const iconSize = numElements === 1 ? 18 : 24;
                        drawModeratorIcon(layerParticipantName, iconX, iconY, iconSize);
                    }
                    // @ts-ignore
                    this.ctx.drawImage(videos[i], elementX, elementY, elementWidth, elementHeight);
                }
            }
            // Controllo finale sicurezza prima del draw
            if (!this.canvas || !this.ctx) {
                console.warn('Canvas perso durante il rendering');
                return;
            }
            this.ctx.drawImage(layerParticipantName.canvas, 0, 0);
            this.paintID = requestAnimationFrame(paint);
        };
        this.paintID = requestAnimationFrame(paint);
        // FPS ottimizzato per fluidità senza sovraccarico
        const fps = 25; // 25 FPS - bilanciato tra fluidità e performance
        // SAFARI FIX: imposta dimensione iniziale del canvas PRIMA del captureStream
        // Safari blocca la risoluzione dello stream al momento della chiamata
        if (this.canvas && this.ctx) {
            const initialWidth = 1920;
            const initialHeight = 1080;
            this.canvas.width = initialWidth;
            this.canvas.height = initialHeight;
            this.canvas.style.width = initialWidth + "px";
            this.canvas.style.height = initialHeight + "px";
            // reset transform per sicurezza
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        }
        if (this.canvas && 'captureStream' in this.canvas) {
            // @ts-ignore
            return this.canvas.captureStream(fps);
        }
        else if (this.canvas && 'webkitCaptureStream' in this.canvas) {
            // @ts-ignore
            return this.canvas.webkitCaptureStream(fps);
        }
        // Fallback se canvas non disponibile
        return null;
    },
    /**
     * Starts a local recording.
     *
     * @param {IStore} store - The redux store.
     * @param {boolean} onlySelf - Whether to record only self streams.
     * @returns {void}
     */
    async startLocalRecording(store, onlySelf) {
        const { dispatch, getState } = store;
        // Salva dispatch per usarlo nelle notifiche
        this.dispatch = dispatch;
        // @ts-ignore
        const supportsCaptureHandle = Boolean(navigator.mediaDevices.setCaptureHandleConfig) && !isEmbedded();
        const tabId = uuidV4();
        this.selfRecording.on = onlySelf;
        this.recordingData = [];
        this.roomName = getRoomName(getState()) ?? '';
        const tracks = getTrackState(getState());
        if (onlySelf) {
            let audioTrack = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)?.jitsiTrack?.track;
            let videoTrack = getLocalTrack(tracks, MEDIA_TYPE.VIDEO)?.jitsiTrack?.track;
            if (!audioTrack) {
                APP.conference.muteAudio(false);
                setTimeout(() => APP.conference.muteAudio(true), 100);
                await new Promise(resolve => {
                    setTimeout(resolve, 100);
                });
            }
            if (videoTrack && videoTrack.readyState !== 'live') {
                videoTrack = undefined;
            }
            audioTrack = getLocalTrack(getTrackState(getState()), MEDIA_TYPE.AUDIO)?.jitsiTrack?.track;
            if (!audioTrack && !videoTrack) {
                throw new Error('NoLocalStreams');
            }
            this.selfRecording.withVideo = Boolean(videoTrack);
            const localTracks = [];
            audioTrack && localTracks.push(audioTrack);
            videoTrack && localTracks.push(videoTrack);
            this.stream = new MediaStream(localTracks);
        }
        else {
            if (supportsCaptureHandle) {
                // @ts-ignore
                navigator.mediaDevices.setCaptureHandleConfig({
                    handle: `JitsiMeet-${tabId}`,
                    permittedOrigins: ['*']
                });
            }
            const localAudioTrack = getLocalTrack(tracks, MEDIA_TYPE.AUDIO)?.jitsiTrack?.track;
            // Starting chrome 107, the recorder does not record any data if the audio stream has no tracks
            // To fix this we create a track for the local user(muted track)
            if (!localAudioTrack) {
                APP.conference.muteAudio(false);
                setTimeout(() => APP.conference.muteAudio(true), 100);
                await new Promise(resolve => {
                    setTimeout(resolve, 100);
                });
            }
            // handle no mic permission
            if (!getLocalTrack(getTrackState(getState()), MEDIA_TYPE.AUDIO)?.jitsiTrack?.track) {
                throw new Error('NoMicTrack');
            }
            const currentTitle = document.title;
            document.title = i18next.t('localRecording.selectTabTitle');
            this.canvas = document.createElement('canvas');
            // Ottimizzazioni canvas conservative per compatibilità universale
            this.canvas.style.imageRendering = 'auto'; // Standard più compatibile
            (document.body || document.documentElement).appendChild(this.canvas);
            this.ctx = this.canvas.getContext('2d', {
                willReadFrequently: true,
                alpha: false, // Performance senza trasparenza
                desynchronized: false, // Conservative per compatibilità
                powerPreference: 'default' // Compatibile con tutti i device
            });
            document.title = currentTitle;
            this.initializeAudioMixer();
            const allTracks = getTrackState(getState());
            allTracks.forEach((track) => {
                if (track.mediaType === MEDIA_TYPE.AUDIO) {
                    const audioTrack = track?.jitsiTrack?.track;
                    this.addAudioTrackToLocalRecording(audioTrack);
                }
            });
            const videoStream = this.recordVideos();
            this.stream = new MediaStream([
                ...this.audioDestination?.stream.getAudioTracks() || [],
                ...(videoStream ? videoStream.getTracks() : [])
            ]);
        }
        // Lascia che il browser gestisca automaticamente qualità e bitrate
        // per massima compatibilità cross-platform
        const recorderOptions = {
            mimeType: this.mediaType
            // Rimosse specifiche bitrate per lasciare decidere al browser
            // Parametri automatici = maggiore compatibilità
        };
        // Opzioni minimali per massima compatibilità cross-platform
        if (!onlySelf) {
            // Solo per registrazioni multi-partecipanti
            // Lasciamo che il browser gestisca automaticamente i parametri
        }
        this.recorder = new MediaRecorder(this.stream, recorderOptions);
        this.forceStopAndSave = () => {
            const recorder = this.recorder;
            if (!recorder || recorder.state === 'inactive') {
                return;
            }
            // Se abbiamo già salvato, non fare nulla
            if (this.recordingSaved) {
                return;
            }
            // SALVA IMMEDIATAMENTE PRIMA di fermare il recorder
            // Questo garantisce il salvataggio anche se la pagina si chiude subito dopo
            if (this.recordingData.length > 0) {
                this.recordingSaved = true; // Setta il flag SOLO se stiamo salvando
                const dataToSave = [...this.recordingData];
                this.recordingData = []; // Svuota PRIMA del salvataggio per evitare duplicati
                this.saveRecording(dataToSave, this.getFilename());
            }
            this.recorder = undefined; // lock immediato per evitare chiamate multiple
            try {
                recorder.stop();
                // Salvataggio già fatto sopra - non pulire flag qui perché
                // la pulizia viene gestita a monte nel meetingLeaveHandler
            }
            catch (error) {
                console.error('Errore durante stop forzato:', error);
                // Anche in caso di errore, tenta di salvare
                if (this.recordingData.length > 0 && !this.recordingSaved) {
                    this.recordingSaved = true;
                    const dataToSave = [...this.recordingData];
                    this.recordingData = [];
                    this.saveRecording(dataToSave, `${this.getFilename()}_rescue`);
                }
            }
        };
        // Il popup nativo non è necessario - usiamo solo pagehide per il vero abbandono
        // Handler per uscita dal meeting tramite interfaccia (click bottone rosso "Chiudi")
        this.meetingLeaveHandler = () => {
            if (this.isRecordingLocally()) {
                this.isLeavingFromButton = true; // Segna che sta uscendo dal bottone
                // USCITA NORMALE (bottone): pulisci SUBITO tutti i flag restart
                // per evitare auto-restart spurio al rientro
                try {
                    localStorage.removeItem('jitsi_was_recording_before_reload');
                    localStorage.removeItem('jitsi_recording_timestamp');
                    localStorage.removeItem('jitsi_recording_onlyself');
                    localStorage.removeItem('jitsi_reload_from_stay');
                    localStorage.setItem('jitsi_last_meeting_exit_type', 'button');
                    localStorage.setItem('jitsi_last_meeting_exit', Date.now().toString());
                }
                catch (e) {
                }
                this.forceStopAndSave?.();
            }
        };
        this.recorder.addEventListener('dataavailable', e => {
            if (e.data && e.data.size > 0 && !this.recordingSaved) {
                this.recordingData.push(e.data);
                // Controllo memoria invece di totalSize
                this.checkMemoryUsage();
                // Controllo critico: mostra notifica più urgente se ancora più memoria (solo una volta)
                if (this.currentMemoryUsage > MEMORY_SAFETY_THRESHOLD * 3 && !this.criticalMemoryWarningShown) {
                    this.criticalMemoryWarningShown = true;
                    if (this.dispatch) {
                        this.dispatch(showWarningNotification({
                            titleKey: 'recording.criticalMemoryUsageTitle',
                            descriptionKey: 'recording.criticalMemoryUsageMessage'
                        }, NOTIFICATION_TIMEOUT_TYPE.STICKY));
                    }
                    else {
                        console.warn(i18next.t('recording.criticalMemoryUsageMessage'));
                    }
                }
            }
        });
        if (!onlySelf) {
            this.recorder.addEventListener('stop', () => {
                this.stream?.getTracks().forEach((track) => track.stop());
                cancelAnimationFrame(this.paintID);
            });
            this.stream.addEventListener('inactive', () => {
                if (this.isRecordingLocally()) {
                    this.dispatch?.(stopLocalVideoRecording(true));
                }
            });
        }
        // Intervallo ottimizzato per stabilità e compatibilità
        const recordingInterval = onlySelf ? 4000 : 3000; // Bilanciato per memoria e stabilità
        this.recordingTimeslice = recordingInterval;
        this.recorder.start(recordingInterval);
        // Reset contatori per registrazione pulita
        this.currentMemoryUsage = 0;
        this.memoryWarningShown = false;
        this.criticalMemoryWarningShown = false;
        this.recordingSaved = false;
        // Safety net: salva se la pagina si chiude
        window.addEventListener('pagehide', () => {
            if (this.recordingData.length > 0 && !this.recordingSaved) {
                this.recordingSaved = true;
                const dataToSave = [...this.recordingData];
                this.recordingData = [];
                this.saveRecording(dataToSave, this.getFilename());
            }
        });
        // Protezione disconnessione di rete
        // NB: dispatchiamo l'azione redux (invece di chiamare forceStopAndSave
        // direttamente) così lo stato di registrazione viene aggiornato e
        // propagato agli altri partecipanti tramite la presence; altrimenti
        // il file viene salvato ma tutti continuano a vedere l'icona di
        // registrazione attiva.
        window.addEventListener('offline', () => {
            if (this.isRecordingLocally()) {
                this.dispatch?.(stopLocalVideoRecording(true));
            }
        });
        // Protezione perdita connessione WebRTC
        if (this.stream) {
            this.stream.addEventListener('inactive', () => {
                if (this.isRecordingLocally()) {
                    this.dispatch?.(stopLocalVideoRecording(true));
                }
            });
        }
    },
    /**
     * Whether or not we're currently recording locally.
     *
     * @returns {boolean}
     */
    isRecordingLocally() {
        return Boolean(this.recorder && this.recorder.state === 'recording');
    },
    /**
     * Whether or not local recording is supported in the current environment.
     *
     * @returns {boolean}
     */
    isSupported() {
        return Boolean(MediaRecorder
            && navigator.mediaDevices
            && navigator.mediaDevices.getDisplayMedia);
    },
    /**
     * Stops the auto-save interval.
     *
     * @returns {void}
     */
    stopAutoSave() {
        if (this.recordInterval) {
            clearInterval(this.recordInterval);
            this.recordInterval = undefined;
        }
    },
    forceStopAndSave: null,
    meetingLeaveHandler: null
};
export default LocalRecordingManager;
